<script lang="ts" setup>
import { useId, useSlots } from 'vue';
import { useMagicKeys, whenever } from '@vueuse/core';
import VSizeContainer, {
  type ContainerSize,
} from '@shared/layout/VSizeContainer/VSizeContainer.vue';
import VButtonGroup from '@shared/component/VButtonGroup/VButtonGroup.vue';
import { useApplicationRoot } from '@tools/applicationRoot';
import { useScrollLock } from '@shared/layout/useScrollLock';
import VButton from './VButtonModal.vue';
import { type ButtonType } from '../VButton/vButtonTypes';
import VContentBox from '../VContentBox/VContentBox.vue';
import { ModalType } from '../types';

export interface VModalProps {
  isActive?: boolean;
  disabled?: boolean;
  type?: ModalType;
  /**
   * Bevat een korte tekst die matcht met de rest van de modalteksten.
   *
   * Gebruik `props.type` om een passend visueel onderscheidend uiterlijk in te stellen.
   * @example 'Verwijderen'
   */
  confirmButtonText?: string;
  cancelButtonText?: string;
  denyButtonText?: string;
  loading?: boolean;
  overflow?: boolean;
  /**
   * Kort en meestal in vragende vorm.
   * @example 'Product verwijderen?'
   */
  title?: string;
  invertHeader?: boolean;
  dataTestid?: string;
  size?: ContainerSize;
  isIframe?: boolean;
  /**
   * Schakelt de standaard padding uit voor custom layouts
   */
  noPadding?: boolean;
}

const props = withDefaults(defineProps<VModalProps>(), {
  dataTestId: undefined,
  isActive: false,
  disabled: false,
  type: ModalType.Success,
  confirmButtonText: 'Opslaan',
  cancelButtonText: 'Annuleren',
  denyButtonText: '',
  overflow: false,
  invertHeader: false,
  noPadding: false,
  size: 'deprecated_768',
});

const emit = defineEmits<{
  closeModal: [value?: Event];
  close: [value?: Event];
  confirmAction: [value?: Event];
  denyAction: [value?: Event];
  cancelButtonClicked: [value?: Event];
}>();

defineSlots<{
  /**
   * Legt uit wat de gevolgen zijn en/of bevat aanvullende informatie.
   * @example "Als je Voorbeeldproduct verwijdert, kan dit niet ongedaan gemaakt worden. Je collega's ontvangen hiervan een melding per e-mail."
   */
  default(): any;
  footer(): any;
  /**
   * Volledige controle over de header content. Wanneer gebruikt, worden title en header-end genegeerd.
   */
  header(): any;
  'header-end'(): any;
}>();

useScrollLock(() => props.isActive);

const applicationroot = useApplicationRoot();

const slots = useSlots();
const keys = useMagicKeys();
const titleId = useId();
whenever(keys.escape, () => close());

function close() {
  emit('closeModal');
  emit('close');
}

const getButtonType = (): ButtonType => {
  switch (props.type) {
    case ModalType.Danger:
      return 'danger';
    case ModalType.Success:
    default:
      return 'primary';
  }
};

function cancel() {
  emit('cancelButtonClicked');
  close();
}
</script>

<template>
  <Teleport :to="applicationroot" defer>
    <div
      v-show="props.isActive"
      class="fixed inset-0 z-overlay grid min-w-[50%] animate-fade place-items-center animation-duration-300"
      :data-testid="props.dataTestid"
      role="dialog"
      :aria-labelledby="titleId"
    >
      <div class="absolute inset-0 bg-grey-800/80" @click="close()"></div>
      <VSizeContainer v-if="!props.isIframe" :size="props.size">
        <VContentBox
          class="grid max-h-dvh grid-rows-[auto_minmax(0,1fr)]"
          :margin-bottom="0"
          :title="slots.header ? undefined : props.title"
          :title-id
          :invert-header="invertHeader"
          :no-padding="props.noPadding"
          :class="{
            'overflow-visible': props.overflow,
            'overflow-hidden': !props.overflow,
          }"
        >
          <template v-if="slots.header" #header>
            <slot name="header"></slot>
          </template>
          <template v-else-if="slots['header-end']" #header-end>
            <slot name="header-end"></slot>
          </template>
          <div class="grid max-h-full grid-rows-[minmax(0,1fr)_auto] gap-4">
            <section
              class="z-dialog"
              :class="[
                !props.noPadding && '-m-4 -mt-6 mb-0 p-4 pb-2 pt-6',
                {
                  'overflow-visible': props.overflow,
                  'overflow-auto': !props.overflow,
                },
              ]"
            >
              <slot></slot>
            </section>

            <footer class="flex justify-end gap-2">
              <slot v-if="slots.footer" name="footer"></slot>
              <VButtonGroup v-else>
                <VButton inverted v-if="props.cancelButtonText" @click.stop="cancel">
                  {{ props.cancelButtonText }}
                </VButton>
                <VButton
                  :disabled="props.disabled"
                  v-if="props.denyButtonText"
                  @click="emit('denyAction')"
                  type="danger"
                >
                  {{ props.denyButtonText }}
                </VButton>
                <VButton
                  v-if="props.confirmButtonText"
                  :disabled="props.disabled"
                  :type="getButtonType()"
                  :loading="props.loading"
                  @click.stop="emit('confirmAction')"
                >
                  {{ props.confirmButtonText }}
                </VButton>
              </VButtonGroup>
            </footer>
          </div>
        </VContentBox>
      </VSizeContainer>

      <div
        v-else
        class="z-dialog mx-8 h-[calc(80dvh-77px)] w-full max-w-[90dvw] rounded-t bg-white shadow-lg"
      >
        <slot></slot>
      </div>
    </div>
  </Teleport>
</template>
